import React from 'react';

/**
 * Section + SectionHeading — the layout backbone of the site.
 *
 * Every page section goes through here so vertical rhythm, container width and
 * surface tone stay consistent. `tone` is the only knob that changes background,
 * which is what creates the deliberate light → tinted → dark cadence down a page
 * instead of the previous "everything is white or slate-800" flatness.
 *
 * Server component. No client JS.
 */

type Tone = 'default' | 'alt' | 'dark' | 'accent';

const toneClass: Record<Tone, string> = {
  default: 'bg-background text-foreground',
  alt: 'bg-surface-2 text-foreground',
  // Layered gradients (see tailwind.css) — flat navy/blue read as lifeless.
  dark: 'bg-dark-rich text-white',
  accent: 'bg-accent-rich text-accent-foreground',
};

export function Section({
  tone = 'default',
  size = 'default',
  className = '',
  children,
  ...rest
}: {
  tone?: Tone;
  size?: 'default' | 'tight';
  className?: string;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children'>) {
  return (
    <section
      className={`${size === 'tight' ? 'section-tight' : 'section'} ${toneClass[tone]} ${className}`}
      {...rest}
    >
      <div className="container-page">{children}</div>
    </section>
  );
}

/**
 * Eyebrow → title → lead. A single component so every section header on the site
 * has identical spacing and type. `align` defaults to left: centred headings
 * everywhere is one of the strongest "template" tells, so centring is now opt-in
 * and used only for full-width CTAs.
 */
export function SectionHeading({
  eyebrow,
  title,
  lead,
  align = 'left',
  tone = 'light',
  as: Heading = 'h2',
  className = '',
}: {
  eyebrow?: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  align?: 'left' | 'center';
  tone?: 'light' | 'dark';
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
}) {
  const dark = tone === 'dark';
  return (
    <div
      className={`${align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'} ${className}`}
    >
      {eyebrow && (
        <p className={`t-eyebrow ${dark ? 'text-accent' : 'text-accent-strong'}`}>{eyebrow}</p>
      )}
      <Heading className={`${Heading === 'h1' ? 't-h1' : 't-h2'} ${eyebrow ? 'mt-3' : ''}`}>
        {title}
      </Heading>
      {lead && (
        <p className={`t-body mt-4 ${dark ? 'text-slate-300' : 'text-muted-foreground'}`}>{lead}</p>
      )}
    </div>
  );
}

/**
 * SectionHeader — THE sitewide dark section heading, standardised on the About
 * "What Drives Us" reference: bold white title with one gradient keyword,
 * uppercase eyebrow BENEATH the title flanked by hairlines, then a lead with a
 * controlled measure. `align="left"` keeps in-panel headers left-aligned with a
 * single trailing hairline. `divider` renders the lens divider for headers that
 * have no eyebrow (Vision & Mission). Mark the keyword with <Grad>.
 */
export function SectionHeader({
  title,
  eyebrow,
  lead,
  align = 'center',
  divider = false,
  reveal = true,
  as: Heading = 'h2',
  className = '',
  headingClassName = '',
  eyebrowClassName = '',
}: {
  title: React.ReactNode;
  eyebrow?: string;
  lead?: React.ReactNode;
  align?: 'center' | 'left';
  divider?: boolean;
  /** Scroll-reveals the header once (default). Disable where a section runs
      its own entrance animation. */
  reveal?: boolean;
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
  headingClassName?: string;
  eyebrowClassName?: string;
}) {
  const center = align === 'center';
  return (
    <div
      {...(reveal ? { 'data-reveal': '' } : {})}
      className={`${center ? 'mx-auto max-w-3xl text-center' : 'max-w-2xl'} ${className}`}
    >
      <Heading
        className={`font-bold text-white ${headingClassName}`}
        style={{
          fontSize: 'clamp(2rem, 1rem + 3vw, 3.5rem)',
          lineHeight: 1.08,
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </Heading>
      {eyebrow && (
        <p
          className={`mt-4 flex items-center gap-3 text-[0.8125rem] font-semibold uppercase tracking-[0.2em] text-accent ${
            center ? 'justify-center' : ''
          } ${eyebrowClassName}`}
        >
          {center && (
            <span
              aria-hidden="true"
              className="h-px w-10 bg-gradient-to-r from-transparent to-accent/70"
            />
          )}
          {eyebrow}
          <span
            aria-hidden="true"
            className="h-px w-10 bg-gradient-to-l from-transparent to-accent/70"
          />
        </p>
      )}
      {divider && (
        <div
          aria-hidden="true"
          className={`mt-5 h-[2px] w-28 rounded-full bg-gradient-to-r from-transparent via-accent to-transparent shadow-[0_0_14px_rgba(14,165,233,0.5)] ${
            center ? 'mx-auto' : ''
          }`}
        />
      )}
      {lead && <p className={`t-body mt-5 text-slate-300 ${center ? 'mx-auto' : ''}`}>{lead}</p>}
    </div>
  );
}

/** The gradient keyword inside a SectionHeader title. */
export function Grad({
  children,
  block = false,
  className = '',
}: {
  children: React.ReactNode;
  block?: boolean;
  className?: string;
}) {
  return (
    <span className={`text-accent-grad ${block ? 'block ' : ''}${className}`}>{children}</span>
  );
}

/**
 * A labelled figure. Used for material characteristics and case-study results.
 * `tabular-nums` keeps columns of figures optically aligned.
 */
export function Stat({
  value,
  label,
  note,
  tone = 'light',
}: {
  value: string;
  label: string;
  note?: string;
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <div className="min-w-0">
      <div className={`t-value text-2xl font-semibold ${dark ? 'text-white' : 'text-foreground'}`}>
        {value}
      </div>
      <div className={`mt-1 text-sm ${dark ? 'text-slate-400' : 'text-muted-foreground'}`}>
        {label}
      </div>
      {note && (
        <div className={`mt-0.5 text-xs ${dark ? 'text-slate-500' : 'text-muted-foreground/80'}`}>
          {note}
        </div>
      )}
    </div>
  );
}
